import React from "react";
import {
  Audio,
  Composition,
  Img,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";

export const KyuStorePromoComposition: React.FC = () => {
  return (
    <Composition
      id="KyuStorePromo"
      component={KyuStorePromoView}
      durationInFrames={180} // 6.0 seconds @ 30fps
      fps={30}
      width={1080}
      height={1920}
    />
  );
};

export const KyuStorePromoView: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 1. Entrance Springs
  const badgeSpring = spring({
    frame: frame - 5,
    fps,
    config: { damping: 12, stiffness: 120 },
  });

  const headlineSpring = spring({
    frame: frame - 15,
    fps,
    config: { damping: 10, mass: 0.8, stiffness: 140 },
  });

  const heroSpring = spring({
    frame: frame - 20,
    fps,
    config: { damping: 14, mass: 1, stiffness: 90 },
  });

  // Staggered Cards (Scene 2)
  const card1Spring = spring({
    frame: frame - 40,
    fps,
    config: { damping: 12, stiffness: 110 },
  });

  const card2Spring = spring({
    frame: frame - 50,
    fps,
    config: { damping: 12, stiffness: 110 },
  });

  const card3Spring = spring({
    frame: frame - 60,
    fps,
    config: { damping: 12, stiffness: 110 },
  });

  // CTA & Footer (Scene 3)
  const ctaSpring = spring({
    frame: frame - 85,
    fps,
    config: { damping: 11, stiffness: 120 },
  });

  // Ambient floating animation
  const floatY = Math.sin(frame / 15) * 12;
  const pulseScale = 1 + Math.sin(frame / 10) * 0.025;

  // Background light sweep
  const sweepX = interpolate(frame, [0, 150], [-500, 1500]);

  return (
    <div
      style={{
        flex: 1,
        width: "100%",
        height: "100%",
        backgroundColor: "#0A061A",
        position: "relative",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        color: "#ffffff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        boxSizing: "border-box",
        padding: "80px 60px 70px",
      }}
    >
      {/* Audio Voiceover via Microsoft Edge TTS */}
      <Audio src={staticFile("voiceover.mp3")} />

      {/* Background radial gradient layers */}
      <div
        style={{
          position: "absolute",
          top: -200,
          right: -200,
          width: 900,
          height: 900,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(93,63,211,0.4) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 200,
          left: -200,
          width: 800,
          height: 800,
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(0,240,255,0.15) 0%, transparent 70%)",
          filter: "blur(70px)",
          pointerEvents: "none",
        }}
      />

      {/* Subtle grid texture overlay */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          pointerEvents: "none",
        }}
      />

      {/* TOP SECTION: Branding & Badges */}
      <div style={{ position: "relative", zIndex: 10 }}>
        {/* Top bar pills */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            opacity: badgeSpring,
            transform: `translateY(${(1 - badgeSpring) * -40}px)`,
          }}
        >
          {/* Brand Pill */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: "linear-gradient(135deg, rgba(93,63,211,0.6), rgba(30,20,70,0.8))",
              border: "1px solid rgba(147,112,219,0.5)",
              padding: "12px 28px",
              borderRadius: 999,
              boxShadow: "0 8px 24px rgba(93,63,211,0.35)",
            }}
          >
            <span style={{ fontSize: 24, color: "#FFD700" }}>⚡</span>
            <span style={{ fontSize: 26, fontWeight: 900, letterSpacing: 1.5, textTransform: "uppercase" }}>
              KYU STORE
            </span>
          </div>

          {/* Instant Delivery Badge */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "rgba(0, 240, 255, 0.12)",
              border: "1px solid rgba(0, 240, 255, 0.4)",
              padding: "12px 24px",
              borderRadius: 999,
            }}
          >
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: "50%",
                backgroundColor: "#00F0FF",
                boxShadow: "0 0 12px #00F0FF",
              }}
            />
            <span style={{ fontSize: 22, fontWeight: 700, color: "#00F0FF", letterSpacing: 1 }}>
              PROSES OTOMATIS 1 DETIK
            </span>
          </div>
        </div>

        {/* Big Kinetic Headline */}
        <div
          style={{
            marginTop: 45,
            opacity: headlineSpring,
            transform: `scale(${0.8 + headlineSpring * 0.2}) translateY(${(1 - headlineSpring) * 40}px)`,
          }}
        >
          <div
            style={{
              fontSize: 32,
              fontWeight: 800,
              color: "#A5AFCD",
              letterSpacing: 4,
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            MOBILE LEGENDS BANG BANG
          </div>
          <div
            style={{
              fontSize: 82,
              fontWeight: 900,
              lineHeight: 1.05,
              textTransform: "uppercase",
              background: "linear-gradient(180deg, #FFFFFF 0%, #D4DCFF 60%, #90A0D0 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "drop-shadow(0 10px 30px rgba(0,0,0,0.6))",
            }}
          >
            FLASH SALE <br />
            <span
              style={{
                background: "linear-gradient(90deg, #00F0FF 0%, #7000FF 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              DIAMOND MURAH
            </span>
          </div>
        </div>
      </div>

      {/* MIDDLE SECTION: Hero Character + Pricing Cards */}
      <div
        style={{
          position: "relative",
          zIndex: 15,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          margin: "20px 0",
        }}
      >
        {/* MLBB Hero Cutout (Positioned on the Right Background) */}
        <div
          style={{
            position: "absolute",
            right: -130,
            top: -40,
            width: 780,
            height: 980,
            opacity: heroSpring,
            transform: `translateX(${(1 - heroSpring) * 150}px) translateY(${floatY}px)`,
            pointerEvents: "none",
            zIndex: 1,
            filter: "drop-shadow(-15px 15px 35px rgba(0, 0, 0, 0.7))",
          }}
        >
          <Img
            src={staticFile("hero.png")}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
            }}
          />
        </div>

        {/* Pricing Cards Stack (Left Side) */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 22,
            width: "560px",
            zIndex: 5,
          }}
        >
          {/* Card 1: 86 Diamonds */}
          <div
            style={{
              opacity: card1Spring,
              transform: `translateX(${(1 - card1Spring) * -80}px)`,
              background: "rgba(18, 12, 42, 0.85)",
              border: "1.5px solid rgba(110, 80, 200, 0.4)",
              backdropFilter: "blur(25px)",
              borderRadius: 24,
              padding: "24px 30px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 15px 35px rgba(0, 0, 0, 0.4)",
            }}
          >
            <div>
              <div style={{ fontSize: 22, color: "#A5AFCD", fontWeight: 700, textTransform: "uppercase" }}>
                Paket Hemat
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#FFFFFF" }}>
                86 Diamonds
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 18, color: "#8590B5", textDecoration: "line-through" }}>
                Rp 23.000
              </div>
              <div style={{ fontSize: 38, fontWeight: 900, color: "#00F0FF" }}>
                Rp 19.500
              </div>
            </div>
          </div>

          {/* Card 2: 172 Diamonds */}
          <div
            style={{
              opacity: card2Spring,
              transform: `translateX(${(1 - card2Spring) * -80}px)`,
              background: "rgba(18, 12, 42, 0.85)",
              border: "1.5px solid rgba(110, 80, 200, 0.4)",
              backdropFilter: "blur(25px)",
              borderRadius: 24,
              padding: "24px 30px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 15px 35px rgba(0, 0, 0, 0.4)",
            }}
          >
            <div>
              <div style={{ fontSize: 22, color: "#A5AFCD", fontWeight: 700, textTransform: "uppercase" }}>
                Paling Laris
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#FFFFFF" }}>
                172 Diamonds
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 18, color: "#8590B5", textDecoration: "line-through" }}>
                Rp 45.000
              </div>
              <div style={{ fontSize: 38, fontWeight: 900, color: "#00F0FF" }}>
                Rp 38.900
              </div>
            </div>
          </div>

          {/* Card 3: Weekly Diamond Pass (Featured) */}
          <div
            style={{
              opacity: card3Spring,
              transform: `translateX(${(1 - card3Spring) * -80}px) scale(${pulseScale})`,
              background: "linear-gradient(135deg, rgba(35, 20, 80, 0.95), rgba(93, 63, 211, 0.4))",
              border: "2px solid #FFB300",
              backdropFilter: "blur(25px)",
              borderRadius: 24,
              padding: "26px 30px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: "0 20px 45px rgba(255, 179, 0, 0.25)",
              position: "relative",
            }}
          >
            {/* Best Deal ribbon */}
            <div
              style={{
                position: "absolute",
                top: -14,
                left: 28,
                background: "linear-gradient(90deg, #FFB300, #FF8000)",
                color: "#000000",
                fontSize: 16,
                fontWeight: 900,
                letterSpacing: 1.5,
                padding: "4px 18px",
                borderRadius: 999,
                textTransform: "uppercase",
              }}
            >
              ★ REKOMENDASI TERBAIK
            </div>
            <div>
              <div style={{ fontSize: 22, color: "#FFD700", fontWeight: 800 }}>
                WEEKLY PASS
              </div>
              <div style={{ fontSize: 36, fontWeight: 900, color: "#FFFFFF" }}>
                210+ Diamonds
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 18, color: "#B5A080", textDecoration: "line-through" }}>
                Rp 32.000
              </div>
              <div style={{ fontSize: 40, fontWeight: 900, color: "#FFD700" }}>
                Rp 26.500
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM SECTION: Call to Action & Website Footer */}
      <div
        style={{
          position: "relative",
          zIndex: 20,
          opacity: ctaSpring,
          transform: `translateY(${(1 - ctaSpring) * 60}px)`,
        }}
      >
        {/* Main CTA Button */}
        <div
          style={{
            background: "linear-gradient(90deg, #00F0FF 0%, #5D3FD3 50%, #A855F7 100%)",
            borderRadius: 28,
            padding: "32px 40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 20px 50px rgba(93, 63, 211, 0.5)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Light shine effect */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: sweepX,
              width: 150,
              height: "100%",
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
              transform: "skewX(-25deg)",
              pointerEvents: "none",
            }}
          />

          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 22, color: "rgba(255,255,255,0.9)", fontWeight: 700, letterSpacing: 2 }}>
              KUNJUNGI SEKARANG
            </span>
            <span style={{ fontSize: 46, fontWeight: 900, color: "#FFFFFF", letterSpacing: 1 }}>
              KYUSTORE.COM
            </span>
          </div>

          <div
            style={{
              background: "#FFFFFF",
              color: "#0A061A",
              width: 68,
              height: 68,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 34,
              fontWeight: 900,
              boxShadow: "0 8px 20px rgba(0,0,0,0.3)",
            }}
          >
            ➜
          </div>
        </div>

        {/* Footer Support Badges */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 35,
            marginTop: 26,
            color: "#8590B5",
            fontSize: 20,
            fontWeight: 600,
          }}
        >
          <span>✓ QRIS SEMUA BANK / E-WALLET</span>
          <span>•</span>
          <span>✓ 100% LEGAL & AMAN</span>
          <span>•</span>
          <span>✓ LAYANAN 24/7 CS CEPAT</span>
        </div>
      </div>
    </div>
  );
};
